import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send, Phone, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { COMPANY, SERVICE_OPTIONS } from "../data/site";
import { Reveal, Overline, PageHeader } from "../components/Reveal";

const EMPTY = { name: "", email: "", phone: "", city: "", service: "", message: "" };

export default function Quote() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) return toast.error("Veuillez sélectionner un service.");
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        overline="Devis gratuit"
        title="Demandez votre devis personnalisé"
        subtitle="Décrivez votre projet en quelques clics. Nous vous recontactons rapidement avec une estimation claire et sans engagement."
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-3">
          <div className="bg-white border border-border rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            {done ? (
              <div className="text-center py-12" data-testid="quote-success">
                <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-9 h-9 text-brand" />
                </div>
                <h2 className="font-display text-2xl font-extrabold text-navy mt-6">Merci pour votre demande !</h2>
                <p className="text-text-muted mt-3">Notre équipe vous recontacte très prochainement.</p>
                <button onClick={() => setDone(false)} className="mt-7 bg-navy text-white font-bold py-3 px-7 rounded-full hover:bg-navy-light transition-colors">
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Nom complet *">
                    <input required value={form.name} onChange={set("name")} data-testid="quote-name" className={inputCls} placeholder="Jean Dupont" />
                  </Field>
                  <Field label="Téléphone *">
                    <input required value={form.phone} onChange={set("phone")} data-testid="quote-phone" className={inputCls} placeholder="06 12 34 56 78" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email *">
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="quote-email" className={inputCls} placeholder="jean@email.fr" />
                  </Field>
                  <Field label="Ville">
                    <input value={form.city} onChange={set("city")} data-testid="quote-city" className={inputCls} placeholder="Angers" />
                  </Field>
                </div>
                <Field label="Service concerné *">
                  <select required value={form.service} onChange={set("service")} data-testid="quote-service" className={inputCls}>
                    <option value="">Sélectionnez un service…</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Votre projet / message">
                  <textarea rows={5} value={form.message} onChange={set("message")} data-testid="quote-message" className={`${inputCls} resize-none`} placeholder="Décrivez votre besoin, la surface, le type de logement…" />
                </Field>
                <button type="submit" disabled={loading} data-testid="quote-submit" className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {loading ? "Envoi…" : <>Envoyer ma demande <Send className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-text-muted text-center">En envoyant ce formulaire, vous acceptez d'être recontacté par MS Énergie.</p>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-2 space-y-6">
          <div className="bg-navy text-white rounded-3xl p-8">
            <h3 className="font-display font-bold text-xl">Pourquoi nous choisir ?</h3>
            <ul className="space-y-4 mt-6">
              {[
                { icon: BadgeCheck, t: "Devis 100% gratuit", d: "Sans engagement de votre part." },
                { icon: Clock, t: "Réponse rapide", d: "Nous revenons vers vous au plus vite." },
                { icon: ShieldCheck, t: "Travail garanti", d: "Des techniciens qualifiés et assurés." },
              ].map((b) => (
                <li key={b.t} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><b.icon className="w-5 h-5 text-brand" /></div>
                  <div><p className="font-bold">{b.t}</p><p className="text-white/50 text-sm">{b.d}</p></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-surface border border-border rounded-3xl p-8 text-center">
            <p className="text-text-muted text-sm">Vous préférez nous appeler ?</p>
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center justify-center gap-2 font-display font-extrabold text-2xl text-navy mt-2 hover:text-brand transition-colors">
              <Phone className="w-6 h-6 text-brand" /> {COMPANY.phone}
            </a>
            <p className="text-text-muted text-xs mt-2">{COMPANY.hours}</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

const inputCls =
  "w-full h-13 py-3.5 px-4 bg-surface border border-border rounded-xl text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-navy mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
